import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    computed,
    effect,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import {
    ECustomPageEmbeddedMessageType,
    ICustomPageEmbeddedInitialData,
    ICustomPageEmbeddedPostMessage,
    isCustomPageEmbeddedPostMessage,
} from '@se/cp.common';
import { Subscription, filter, fromEvent, map } from 'rxjs';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { NotificationService } from '../../services/notification.service';

@Component({
    selector: 'se-custom-page-dialog',
    standalone: true,
    templateUrl: './se-custom-page-dialog.component.html',
    styleUrl: './se-custom-page-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MatDialogConfig],
    imports: [MatButtonModule, MatDialogModule, MatProgressSpinnerModule, TranslateModule],
})
export class SeCustomPageDialog implements OnDestroy {
    private readonly authenticateService = inject(AuthenticationService);
    private readonly notificationService = inject(NotificationService);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly dialogRef = inject<MatDialogRef<SeCustomPageDialog>>(MatDialogRef);
    private readonly data = inject<ICustomPageEmbeddedInitialData>(MAT_DIALOG_DATA);

    readonly embedded = viewChild<ElementRef<HTMLIFrameElement>>('embedded');
    readonly paringToken = toSignal(this.authenticateService.getUserParingToken());
    readonly urlToOpen = computed(() => {
        const paringToken = this.paringToken();
        if (!paringToken) return '';
        return `${window.location.origin}${window.location.pathname}#/sso/estensaWEB?pt=${paringToken}&goToMenu=report.embedded_custom_page`;
    });
    readonly sanitizedUrlToOpen = computed(() => {
        const url = this.urlToOpen();
        if (!url) return '';
        return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
    });
    private subscriptions: Subscription[] = [];
    readonly isReady = signal<boolean>(false);
    readonly canSave = signal<boolean>(false);

    constructor() {
        effect(() => {
            const embedded = this.embedded();
            if (!embedded) return;
            const subscription = fromEvent<MessageEvent<ICustomPageEmbeddedPostMessage>>(window, 'message')
                .pipe(
                    filter((event) => isCustomPageEmbeddedPostMessage(event.data)),
                    map((event) => event.data),
                )
                .subscribe(this.postMessageHandler);
            this.subscriptions.push(subscription);
        });
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private sendData = () => {
        const embedded = this.embedded();
        if (!embedded) return;
        const iframe = embedded.nativeElement;
        const message: ICustomPageEmbeddedPostMessage = {
            message: 'cp_embedded_data',
            messageType: ECustomPageEmbeddedMessageType.sendData,
            data: this.data,
        };
        iframe.contentWindow?.postMessage(message);
    };

    private statusChangedHandler = (data: { canSave: boolean }) => {
        this.canSave.set(data.canSave);
    };

    save = () => {
        const embedded = this.embedded();
        if (!embedded) return;
        const iframe = embedded.nativeElement;
        const message: ICustomPageEmbeddedPostMessage = {
            message: 'cp_embedded_data',
            messageType: ECustomPageEmbeddedMessageType.save,
        };
        iframe.contentWindow?.postMessage(message);
    };

    close = () => {
        this.dialogRef.close();
    };

    postMessageHandler = (message: ICustomPageEmbeddedPostMessage) => {
        switch (message.messageType) {
            case ECustomPageEmbeddedMessageType.askData:
                this.isReady.set(true);
                return this.sendData();
            case ECustomPageEmbeddedMessageType.statusChanged:
                return this.statusChangedHandler(message.data);
            case ECustomPageEmbeddedMessageType.closeModal:
                return this.close();
            case ECustomPageEmbeddedMessageType.error:
                return this.notificationService.error(message.data.message, message.data.title);
        }
    };
}
